import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { apiRequest } from "src/async/apiUtils";
import Preloader from "src/components/shared/LoadingAnimations/Preloader";
import { apiUrl } from "src/config/host";
import { getNavigateLink } from "src/helpers/userHelper";
import useUrlQueryParams from "src/hooks/useUrlQueryParams";
import AuthDataLayout from "src/layouts/AuthDataLayout";
import auth from "src/services/auth";
import { encodeRelayState } from "src/helpers/urlBuilder";
import { DefaultForm } from "./DefaultForm";
import { WorkspaceSpecificLoginForm } from "./WorkspaceSpecificLoginForm";
import RecoveryCodeView from "./RecoveryCodeView";
import { useSiteSettings } from "src/context/SiteSettingsContext";
import { useUpdateSocketToken } from "src/context/SocketContext";
import { getCookies } from "src/helpers";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { redirectUrl: queryRedirectUrl } = useUrlQueryParams();
  const { site } = useSiteSettings();
  const updateSocketToken = useUpdateSocketToken();

  const [lockout, setLockout] = useState(0);
  const [disabled, setDisabled] = useState(false);
  const [disabledResendOtp, setDisabledResendOtp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [intervalId, setIntervalId] = useState(null);
  const [requireTwoFactor, setRequireTwoFactor] = useState(false);
  const [loadingTrackLogin, setLoadingTrackLogin] = useState(true);
  const [loadingSsos, setLoadingSsos] = useState(false);
  const [isPreloader, setIsPreloader] = useState(true);
  const [ssos, setSsos] = useState([]);
  const [otpSecond, setOtpSecond] = useState(0);
  const [resendOtpSecond, setResendOtpSecond] = useState(0);

  const [twoFactorDetails, setTwoFactorDetails] = useState(null);
  const [backupCode, setBackupCode] = useState(null);
  const [resDataAfterBackup, setResDataAfterBackup] = useState(null);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);

  const loadSso = useCallback(() => {
    (async () => {
      if (!site?._id) return;
      try {
        const { data: resData } = await apiRequest("GET", "/ssos");
        setSsos(resData.data);
      } catch (err) {
      } finally {
        setLoadingSsos(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site?._id]);

  const manageLockout = (data, intervalId) => {
    setLockout(data?.timer || 60);
    if (intervalId) clearInterval(intervalId);

    const intervalDataId = setInterval(() => {
      setLockout((cur) => {
        if (cur) {
          return cur - 1;
        } else {
          clearInterval(intervalDataId);
          return cur;
        }
      });
    }, 1000);

    setIntervalId(intervalDataId);
  };

  const trackLogin = async (message = "") => {
    try {
      if (!message) setLoadingTrackLogin(true);
      const { data: resData } = await apiRequest("GET", "/portal/login/tracking/count");

      if (resData.status === 200 && resData?.data?.attemp_count > 10 && resData?.data?.timer !== 0) {
        manageLockout(resData.data);
      }
    } catch (err) {
    } finally {
      if (!message) setLoadingTrackLogin(false);
      if (message) {
        setDisabled(false);
        toast.error(message);
      }
    }
  };

  useEffect(() => {
    trackLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loadingSsos && site?._id) {
      if (ssos.length === 1 && site.dual_signin_for_user === false && !site?.workspace_login_enabled) {
        if (site.auth0_verification_url && site.auth0_access_token_field_name) {
          auth.verifySSOAccessToken(site.auth0_access_token_field_name).then((data) => {
            const cookies = getCookies();
            const accessToken = cookies[site.auth0_access_token_field_name];
            updateSocketToken(accessToken);
            if (data.status !== 200) {
              ["/login"].includes(window.location.href)
                ? (window.location.href = `${apiUrl}/login-sso?RelayState=${encodeRelayState({
                    origin: window.location.origin,
                    sso_id: ssos[0]?._id,
                  })}`)
                : setIsPreloader(false);
            }
          });
        } else {
          window.location.href = `${apiUrl}/login-sso?RelayState=${encodeRelayState({
            origin: window.location.origin,
            sso_id: ssos[0]?._id,
          })}`;
        }
      } else {
        setIsPreloader(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSsos, site.auth0_verification_url, site.auth0_access_token_field_name, site.dual_signin_for_user, ssos, site?.workspace_login_enabled, site?._id]);

  const clickWorkspace = async () => {
    try {
      setDisabled(true);
      const { data: resData } = await apiRequest("GET", `/portal/users/workspaces/page`);

      if (resData.status === 200) {
        const navigate_link = getNavigateLink(resData);
        navigate(navigate_link);
      } else {
        toast.error("No workspace found.");
        navigate("/permission-denied");
      }
    } catch (err) {
      toast.error(err.message);
      navigate("/permission-denied");
    } finally {
      setDisabled(false);
    }
  };

  const redirectToWorkspace = async (userId) => {
    if (!userId) {
      navigate("/permission-denied");
      return;
    }

    try {
      setDisabled(true);
      await clickWorkspace();
    } catch (err) {
      navigate("/permission-denied");
    } finally {
      setDisabled(false);
    }
  };

  useEffect(() => {
    loadSso();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadSso]);

  const finalRedirectPath = location.state?.from || queryRedirectUrl || null;

  const onSubmit = async (e, workspace) => {
    try {
      e.preventDefault();
      if (disabled || lockout || otpSecond > 0) return;

      setDisabled(true);
      const { data: resData } = await apiRequest("post", "/portal/auth-login", {
        body: {
          email,
          password,
          workspace_id: workspace?._id,
        },
      });

      if (resData.status === 200) {
        if (resData.requireTwoFactor) {
          setTwoFactorDetails(resData);
          setResendOtpSecond(resData?.two_factor_otp_resend_interval_timeframe);
          setRequireTwoFactor(true);
          setDisabled(false);
        } else if (resData?.data?.seconds && resData?.data?.seconds > 0) {
          setOtpSecond(resData?.data?.seconds);
          toast.error(resData.message);
        } else {
          localStorage.setItem("email", resData?.email);
          localStorage.setItem("accessToken", resData.access_token);
          localStorage.setItem("refreshToken", resData.refresh_token);
          localStorage.setItem("isSubDomain", resData?.isSubDomain);
          updateSocketToken(resData.access_token);

          window.postMessage(
            {
              type: "LOGIN_SUCCESS",
              token: resData.access_token,
            },
            "*",
          );
          if (finalRedirectPath) {
            navigate(finalRedirectPath);
          } else {
            await redirectToWorkspace(resData.userId);
          }
        }
      } else {
        const attemp_count_data = resData?.attemp_count_data || { attemp_count: 0 };

        if (attemp_count_data?.attemp_count > 10) {
          manageLockout(attemp_count_data, intervalId);
        }

        toast.error(resData.message);
      }
    } catch (err) {
      trackLogin(err.message);
    }
  };

  const verifyOtp = async (otp, method) => {
    try {
      if (disabled || otpSecond > 0) return;

      setDisabled(true);
      const { data: resData } = await apiRequest("post", "/portal/otp/verify", {
        body: {
          email,
          password,
          otp,
          method,
        },
      });

      if (resData?.status === 429) {
        setOtpSecond(resData?.data.seconds);
        toast.error(resData.message);
      } else if (resData.status === 200) {
        localStorage.setItem("email", resData?.email);
        localStorage.setItem("accessToken", resData.access_token);
        localStorage.setItem("refreshToken", resData.refresh_token);
        localStorage.setItem("isSubDomain", resData?.isSubDomain);
        if (resData.two_factor_totp_backup_code) {
          setBackupCode(resData.two_factor_totp_backup_code);
          setResDataAfterBackup(resData);
        } else {
          window.postMessage({ type: "LOGIN_SUCCESS", token: resData.access_token }, "*");
          await redirectToWorkspace(resData.userId);
        }
      } else {
        toast.error(resData.message);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDisabled(false);
    }
  };

  const resendOtp = async (e) => {
    try {
      setDisabledResendOtp(true);
      const { data: resData } = await apiRequest("post", "/portal/resend/otp", { body: { email, password } });
      setResendOtpSecond(resData?.data?.seconds);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDisabledResendOtp(false);
    }
  };

  const handleTotpRecovery = async (recovery_code) => {
    try {
      setDisabled(true);
      const { data: resData } = await apiRequest("post", "/portal/recovery/totp", {
        body: { email, password, recovery_code },
      });

      if (resData.status === 200) {
        setTwoFactorDetails(resData);
        setIsRecoveryMode(false);
      } else {
        toast.error(resData.message);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDisabled(false);
    }
  };

  useEffect(() => {
    if (otpSecond > 0) {
      const timer = setInterval(() => {
        setOtpSecond((prev) => {
          if (prev === 1) {
            clearInterval(timer);
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [otpSecond]);

  useEffect(() => {
    if (resendOtpSecond > 0) {
      const timer = setInterval(() => {
        setResendOtpSecond((prev) => {
          if (prev === 1) {
            clearInterval(timer);
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [resendOtpSecond]);

  const [currentStep, setCurrentStep] = useState(site?.workspace_login_enabled ? "workspace_login" : "default_login");

  useEffect(() => {
    setCurrentStep(site?.workspace_login_enabled ? "workspace_login" : "default_login");
  }, [site?.workspace_login_enabled]);

  return (
    <>
      {isPreloader ? (
        <div className="flex  h-full flex-grow items-center justify-center bg-white pt-16">
          <Preloader />
        </div>
      ) : (
        <AuthDataLayout
          title={site?.login_page_title}
          subTitle={site?.login_page_subtitle}>
          {backupCode ? (
            <RecoveryCodeView
              code={backupCode}
              site={site}
              disabled={disabled}
              onContinue={async () => {
                window.postMessage({ type: "LOGIN_SUCCESS", token: resDataAfterBackup.access_token }, "*");
                await redirectToWorkspace(resDataAfterBackup.userId);
              }}
            />
          ) : currentStep === "workspace_login" ? (
            <WorkspaceSpecificLoginForm
              lockout={lockout}
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              trackLogin={trackLogin}
              loginDisabled={disabled}
              onSubmit={onSubmit}
              setCurrentStep={setCurrentStep}
              requireTwoFactor={requireTwoFactor}
              twoFactorDetails={twoFactorDetails}
              otp={otp}
              setOtp={setOtp}
              verifyOtp={verifyOtp}
              resendOtp={resendOtp}
              resendOtpSecond={resendOtpSecond}
              disabledResendOtp={disabledResendOtp}
              otpSecond={otpSecond}
              isRecoveryMode={isRecoveryMode}
              setIsRecoveryMode={setIsRecoveryMode}
            />
          ) : (
            <DefaultForm
              ssos={ssos}
              otp={otp}
              disabled={disabled}
              setOtp={setOtp}
              email={email}
              setEmail={setEmail}
              onSubmit={onSubmit}
              password={password}
              lockout={lockout}
              resendOtp={resendOtp}
              verifyOtp={verifyOtp}
              otpSecond={otpSecond}
              setPassword={setPassword}
              resendOtpSecond={resendOtpSecond}
              requireTwoFactor={requireTwoFactor}
              disabledResendOtp={disabledResendOtp}
              loadingTrackLogin={loadingTrackLogin}
              setCurrentStep={setCurrentStep}
              twoFactorDetails={twoFactorDetails}
              isRecoveryMode={isRecoveryMode}
              setIsRecoveryMode={setIsRecoveryMode}
              handleTotpRecovery={handleTotpRecovery}
            />
          )}
        </AuthDataLayout>
      )}
    </>
  );
};

export default Login;
